<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           :section-modifier="sectionModifier" :menu-item-component="menuItemComponent"
                           :is-tag-legend-hidden="'steak' === sectionModifier"
                           class="menu--section-oceano" :class="containerClasses"
                           menu-item-tag-location=""></default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :section-modifier="sectionModifier" :menu-item-component="menuItemComponent"
                          :is-tag-legend-hidden="'steak' === sectionModifier"
                          class="menu--section-oceano" :class="containerClasses"></default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for WV Steak House default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.0.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2023-09-20 (1.0.0) (jhoward)
 *    - Copy from <WvSteakHouseDefaultSection> component to <oceano-default-section> component.
 *    - <oceano-default-section> assigns a random "fleur" to each section.
 *
 *  2023-10-25 (1.0.1) (jhoward)
 *    - Fix scoping on some global CSS in <oceano-default-section>.
 *
 *  2024-08-19 (1.0.2) (jhoward)
 *    - Add "rules" and "includes" copy section styling to <oceano-default-section>.
 *
 *  2025-02-10 (1.1.0) (jhoward)
 *    - Add <menu-schedules> to the <Oceano_defaultSection> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <Oceano_defaultSection>.
 *    - Add textual and visual menu CSS tweaks to <Oceano_defaultSection>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection, DefaultVisualSection,
  },
  name: "OceanoDefaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: 'oceano-default-item',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    sectionModifier: {
      type: String,
      default: ''
    },
  },
  computed: {
    containerClasses(): { [key: string]: boolean } {
      const output: { [key: string]: boolean }= {
        'menu--section-burgers': this.section.ms_title.toLowerCase().indexOf('burgers') > -1,
      };

      //random fleur for each section
      output['menu--section-title_fleur_' + Math.ceil(Math.random() * 5)] = true;

      return output;
    },
  },
})
</script>

<style scoped>
@font-face {
  font-family: fisherman;
  src: url('/library/fonts/Fisherman-Regular.otf') format('opentype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

.menu--section-oceano {
  font-family: baskerville-display-pt, serif;
}
.menu--section-oceano:deep(.menu--section_title) {
  color: #FFFFFF;
  font-size: 1em;
  font-family: fisherman, serif;
  padding: 0.1em 1em 0.2em 1rem;
  background-color: #042b49;
  border-radius: 0.8em;
  margin: 0 1rem 0 1rem;
  text-align: initial;
  max-width: initial;
}

@media (min-width: 601px) {
  .menu--section-oceano:deep(.menu--section_title) {
    background-image: url(/library/images/menu/oceano-title-bg.png);
    background-repeat: repeat-x;
    background-position: 0 -0.73em;
    background-size: auto 2.4em;
    background-color: initial;
    border-radius: initial;
    overflow: visible;
    height: 1.5em;
    margin: 0;
    padding-left: 0;
  }

  .menu--section-oceano:deep(.menu--section_title span) {
    z-index: 2;
    position: relative;
    text-shadow: #042B48 0 0 4px, #042B48 0 0 4px, #042B48 0 0 4px, #042B48 0 0 4px, #042B48 0 0 4px, #042B48 0 0 4px;
  }

  .textual_menu--section.menu--section-oceano:deep(.menu--section_title) {
    margin-left: 1.5em;
    margin-right: 2em;
  }

  .menu--section-oceano:deep(.menu--section_title::before) {
    content: '';
    position: absolute;
    left: -0.63em;
    width: 0.63em;
    height: 2.35em;
    top: -0.67em;
    background: #FFFFFF url(/library/images/menu/oceano-title-end.png) no-repeat;
    background-size: 100% 100%;
  }

  .menu--section-oceano:deep(.menu--section_title::after) {
    content: '';
    position: absolute;
    top: -0.73em;
    height: 2.39em;
    right: -1.25em;
    width: 100%;
    background-position: 100% 100%;
    background-repeat: no-repeat;
    background-size: auto 100%;
  }

  .menu--section-oceano.menu--section-title_fleur_1:deep(.menu--section_title::after) {
    background-image: url(/library/images/menu/oceano-fleur-1.png);
  }

  .menu--section-oceano.menu--section-title_fleur_2:deep(.menu--section_title::after) {
    background-image: url(/library/images/menu/oceano-fleur-2.png);
  }

  .menu--section-oceano.menu--section-title_fleur_3:deep(.menu--section_title::after) {
    background-image: url(/library/images/menu/oceano-fleur-3.png);
  }

  .menu--section-oceano.menu--section-title_fleur_4:deep(.menu--section_title::after) {
    background-image: url(/library/images/menu/oceano-fleur-4.png);
  }

  .menu--section-oceano.menu--section-title_fleur_5:deep(.menu--section_title::after) {
    background-image: url(/library/images/menu/oceano-fleur-5.png);
  }
}

.menu--section-oceano:deep(.menu--section_subtitle) {
  font-style: italic;
  letter-spacing: initial;
  font-weight: initial;
  padding-right: 2em;
  font-size: 0.8em;
  margin-top: 0.5em;
}

.menu--section-oceano:deep(.menu--section_subgroup_title) {
  text-align: left;
  margin-bottom: 0;
  color: #000;
}

.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--section_subgroup_title span) {
  font-size: 1em;
}

.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup.menu--item_subgroup-show-pricing .menu--section_subgroup_title span:nth-child(n + 2)) {
  text-align: right;
  padding-left: 0;
}

.menu--section-oceano:not(.menu--section-show_pricing_header):deep(.menu--section_subgroup_title) {
  padding-right: 2em;
}

.menu--section-oceano:deep(.menu--item_group) {
  margin-top: 0.5em;
}

.menu--section-oceano:deep(.menu--item_subgroup) {
  margin-top: 0.5em;
  grid-gap: 0.8em;
}

.menu--section-oceano:deep(.menu--item_description) {
  font-style: italic;
}

.menu--section-oceano:deep(.visual_menu--page_section) {
  text-align: center;
  font-style: italic;
  padding-right: 2em;
}

/*.menu--section-oceano:deep(.menu--item:not(.menu--item-price_inline)) {
  grid-template-columns: 1fr 0.3fr;
}*/

@media (max-width: 250px) {
  .menu--section-oceano:deep(.menu--item) {
    display: block;
  }
}

.menu--section-oceano:deep(.menu--item_price) {
  /*text-align: left;
  padding-left: 2em;*/
  font-weight: bold;
}

.menu--section-oceano:deep(.menu--section-meat_temperature::before) {
  content: '';
  position: absolute;
  top: 7.3em;
  left: 6em;
  right: 6em;
  height: 0.125em;
  background-color: #000000;
}

.menu--section-meat_temperature:deep(.menu--item_title) {
  font-weight: 400;
}

.menu--section-meat_temperature:deep(.menu--item) {
  display: block;
}

.menu--section-oceano.menu--section-meat_temperature.menu--section-two_column:deep(.menu--item_subgroup) {
  grid-gap: 0.25em;
}

.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup-show-pricing .menu--section_subgroup_title span:nth-child(n + 2)) {
  text-align: left;
  padding-left: 2em;
}

.menu--section-oceano:deep(.menu--item_subtitle) {
  font-style: italic;
  font-weight: 400;
}

.menu--section-oceano:deep(.menu--item.menu--item-multi_line_price > .menu--item_description) {
  font-style: initial;
}

.menu--section-oceano:deep(.menu--item.menu--item-highlighted) {
  background-color: initial;
  padding-top: 1em;
  position: relative;
}

.menu--section-oceano:not(.menu--section-burgers):deep(.menu--item.menu--item-highlighted::before) {
  content: '';
  position: absolute;
  top: 0;
  left: -1.25em;
  right: -1.25em;
  height: 0.1em;
  background-color: #5b839c;
}

.menu--section-oceano.menu--section-burgers:deep(.menu--item.menu--item-highlighted) {
  border: 0.15em solid #042b49;
  padding: 1em 1.5em;
}

.menu--section-oceano:deep(.visual_menu--page_section.visual_menu--page_section-bottom) {
  padding-top: 1.25em;
}

.menu--section-oceano:deep(.menu--item_subgroup:first-of-type .menu--section_subgroup_title) {
  margin-top: 0;
}

/*.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--item),
.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--section_subgroup_title),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--item),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--section_subgroup_title) {
  grid-template-columns: 2fr 0.3fr 0.3fr;
}

.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--item),
.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--section_subgroup_title),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--item),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--section_subgroup_title) {
  grid-template-columns: 2fr 0.3fr 0.3fr 0.3fr;
}*/


/*.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--item),
.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--section_subgroup_title),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--item),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="2"] .menu--section_subgroup_title) {
  grid-template-columns: 2fr 0.3fr 0.3fr;
}*/

/*.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="2"] .menu--item),
.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="2"] .menu--section_subgroup_title),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="2"] .menu--item),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="2"] .menu--section_subgroup_title) {
  grid-template-columns: 0.15fr 2fr 0.3fr 0.3fr;
}*/

/*.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--item),
.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--section_subgroup_title),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--item),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup[data-pricing-type-count="3"] .menu--section_subgroup_title) {
  grid-template-columns: 2fr 0.3fr 0.3fr 0.3fr;
}*/

/*.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="3"] .menu--item),
.menu--section-oceano.menu--section-show_bin:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="3"] .menu--section_subgroup_title),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="3"] .menu--item),
.menu--section-oceano.menu--section-show_pricing_header:deep(.menu--item_subgroup-show_BIN[data-pricing-type-count="3"] .menu--section_subgroup_title) {
  grid-template-columns: 0.1fr 2fr 0.3fr 0.3fr 0.3fr;
}*/

html .visual_menu--page.first .menu--section-oceano:first-child {
  padding-top: 150px;
}

html .visual_menu--page .menu--section-oceano:first-child {
  padding-bottom: 1em;
}
</style>